import { ModeEditOutlineOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditUserFormModal } from 'pages/SellerPage/components/UsersInfoPage/EditUserFormModal';
import useSellerEditUser from 'pages/SellerPage/hooks/useSellerEditUser';
import useSellerInviteUsers from 'pages/SellerPage/hooks/useSellerInviteUsers';
import { userRoles } from 'shared/constants/constants';

import { InviteUserFormModal } from './InviteUserFormModal';

export const UsersInfoPage = memo(() => {
  const { t } = useTranslation();
  const {
    sellerSendInviteUsers,
    sellerId,
    isLoadingInvitedUsers,
    isPermittedToModifySeller,
    listInvitedUsers,
    refetchInvitedUsers,
  } = useSellerInviteUsers();
  const { sellerEditUser } = useSellerEditUser({ refetchInvitedUsers });
  const [isOpenAddUserModal, setIsOpenAddUserModal] = useState(false);
  const [isOpenEditUserModal, setIsOpenEditUserModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  useEffect(() => {
    // When userId is set, open the edit user modal
    setIsOpenEditUserModal(userToEdit !== null);
  }, [userToEdit]);

  const userRolesObj: Record<string, string> = userRoles.reduce(
    (acc: Record<string, string>, role) => {
      acc[role.id] = role.label;
      return acc;
    },
    {},
  );

  const tableHeaderData = useMemo(
    () => [
      { label: t('Name'), value: 'name' },
      { label: t('Email'), value: 'email' },
      { label: t('Role'), value: 'role' },
      { label: t('Status'), value: 'status' },
    ],
    [t],
  );

  return (
    <Box
      flex="1"
      mt={1}
      p="16px"
      bgcolor="grey.300"
      borderRadius="12px"
      display="flex"
      flexDirection="column"
    >
      {isPermittedToModifySeller && (
        <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
          <Button
            sx={{ width: '212px' }}
            variant="contained"
            onClick={() => setIsOpenAddUserModal(true)}
          >
            {t('Invite User')}
          </Button>
        </Box>
      )}
      <TableContainer sx={{ marginTop: '20px' }}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaderData.map((item) => (
                <TableCell key={item.value}>
                  <Typography
                    sx={{
                      width: '100%',
                      display: 'block',
                    }}
                    typography={'openSans.captionMedium'}
                  >
                    {item.label}
                  </Typography>
                </TableCell>
              ))}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoadingInvitedUsers &&
              (listInvitedUsers && listInvitedUsers.length > 0 ? (
                listInvitedUsers.map((user) => (
                  <TableRow
                    key={user.email}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#E2E9F4',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography typography={'openSans.body2'}>
                        {`${user.lastName}, ${user.firstName}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography typography={'openSans.caption'}>
                        <a
                          href={`mailto:${user.email}`}
                          style={{ color: '#5492F4' }}
                        >
                          {user.email}
                        </a>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography typography={'openSans.body2'}>
                        {userRolesObj[user.roles[0]]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography typography={'openSans.body2'}>
                        {user.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isPermittedToModifySeller && (
                        <IconButton onClick={() => setUserToEdit(user)}>
                          <ModeEditOutlineOutlined color="primary" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography
                      typography={'openSans.body2'}
                      align="center"
                      color="grey.600"
                    >
                      {t('You have not invited any users')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoadingInvitedUsers && (
        <Box display="flex" flexDirection="column" gap="2px">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" height="53px" />
          ))}
        </Box>
      )}
      {isOpenAddUserModal && (
        <InviteUserFormModal
          sellerId={sellerId}
          isOpenAddUserModal={isOpenAddUserModal}
          setIsOpenAddUserModal={setIsOpenAddUserModal}
          sellerSendInviteUsers={sellerSendInviteUsers}
        />
      )}
      {isOpenEditUserModal && (
        <EditUserFormModal
          sellerId={sellerId}
          user={userToEdit}
          isOpenEditUserModal={isOpenEditUserModal}
          handleClose={() => setUserToEdit(null)}
          sellerEditUser={sellerEditUser}
        />
      )}
    </Box>
  );
});
